import React, { useEffect } from 'react';
import { Pressable } from 'react-native';
import { type PlaidLinkOnExit, usePlaidLink } from 'react-plaid-link';
import type {
    PlaidLinkOnEventMetadata,
    PlaidLinkOnSuccessMetadata,
} from 'react-plaid-link/src/types';
import { PlaidLinkStableEvent } from 'react-plaid-link/src/types';

import { captureException, captureMessage } from '../../../logger/Logger';
import type { PlaidLinkProps } from '../../types/Plaid';

const PlaidLink = ({
    children,
    linkToken,
    onSuccess,
    onExit,
}: PlaidLinkProps) => {
    const { open, ready, error, exit } = usePlaidLink({
        token: linkToken ?? '',
        onSuccess: (public_token, metadata: PlaidLinkOnSuccessMetadata) => {
            void onSuccess({ publicToken: public_token, metadata });
        },
        onEvent: (
            eventName: PlaidLinkStableEvent | string,
            metadata: PlaidLinkOnEventMetadata,
        ) => {
            captureMessage(
                `Plaid debugging: ${eventName} ${JSON.stringify(metadata)}`,
            );
            if (
                (eventName as PlaidLinkStableEvent) ===
                PlaidLinkStableEvent.ERROR
            ) {
                captureException(new Error('Plaid error'), {
                    name: `Plaid error ${metadata.error_code ?? ''}: ${
                        metadata.error_type ?? ''
                    } `,
                    message: metadata.error_message ?? '',
                });

                exit({ force: true });
            }
        },
        onExit: onExit as PlaidLinkOnExit | undefined,
    });

    useEffect(() => {
        if (error == null) {
            return;
        }

        captureException(new Error('Failed to initialize Plaid Link'), {
            error,
        });
    }, [error]);

    return (
        <Pressable
            onPress={() => {
                open();
            }}
            style={{ flex: 1 }}
            disabled={!ready}
        >
            {children}
        </Pressable>
    );
};

export default PlaidLink;
